import { getCompanyMatchLabel, type CompanyMatch } from '@tempmee/candidates';
import type { CandidateCardProps } from '@tempmee/react-blocks';
import { isPartialShift } from '~/commonComponents/CommonFunctions';
import { hasSpecialtyMismatch } from '~/hooks/useCandidate';
import { IJob, IMultipleCounters, IUser } from '~/models';
import {
  isBestInDentalWinner,
  toAttendance,
  toRating,
  toShiftCount,
} from '~/utils/candidateBadges';
import { getProfessionalName, toYearsOfExperience } from '~/utils/professional';

/** Whether the counter offer changes the shift's hourly rate. */
export const hasCounterRate = (
  counter: IMultipleCounters | null | undefined,
  job: Partial<IJob> | null | undefined,
): boolean => counter?.candidateRate != null && counter.candidateRate !== job?.rate;

/**
 * Whether the candidate countered the shift's terms at all — changed hours,
 * changed rate, or both. Drives the alert rule that suppresses "Confirm now to
 * secure coverage".
 */
export const hasCounterOffer = (
  counter: IMultipleCounters | null | undefined,
  job: Partial<IJob> | null | undefined,
): boolean => Boolean(isPartialShift(counter)) || hasCounterRate(counter, job);

/**
 * A candidate's signals, as both the list row and the topper read them — the
 * block's own contract, minus the click the topper has no use for.
 */
export type TCandidateCardData = Omit<CandidateCardProps, 'onCardClick'>;

/**
 * Maps the candidate and the two relevant office records into the shared
 * company-match rule. The offer owner determines private-practice vs DSO and
 * supplies a private office's name; the parent account supplies the DSO name.
 */
export const toCompanyMatch = (
  counter: IMultipleCounters | null | undefined,
  {
    offerOwner,
    parentOffice,
  }: {
    offerOwner: IUser | null | undefined;
    parentOffice: IUser | null | undefined;
  },
): CompanyMatch | null =>
  getCompanyMatchLabel({
    averageRating: counter?.candidate?.average_rating,
    badgeCount: counter?.candidate?.badges?.length,
    hasCurrentRank: Boolean(counter?.candidate?.currentRank?.rank),
    hasWorkedWithDSO: counter?.hasWorkedWithDSO,
    hasWorkedWithOffice: counter?.hasWorkedWithOffice,
    officeType: offerOwner?.office_info?.officeType,
    officeName: offerOwner?.office_name,
    dsoFullName: parentOffice?.dsoFullName,
  });

/**
 * Counter offer → one `CandidateCard`.
 *
 * The optional signals are the ones the block draws in a fixed order, so this
 * only decides whether each applies. The company chip uses the same qualified
 * match and label as the Overview statement. `missingSpecialty` is the redesign's
 * replacement for the clickable specialty banner the legacy screen stacked at the
 * top of the page: on a list of candidates the mismatch belongs to a candidate,
 * not to the shift. It reads `hasSpecialtyMismatch`, not `!hasJobSpecialtyMatch`
 * — a shift that names no specialty flags nobody.
 */
export const toCandidateCardData = ({
  counter,
  job,
  companyMatch,
}: {
  counter: IMultipleCounters;
  job: Partial<IJob> | null | undefined;
  companyMatch: CompanyMatch | null;
}): TCandidateCardData => {
  const candidate = counter?.candidate;

  return {
    name: getProfessionalName(candidate) ?? '',
    avatarUrl: candidate?.profile_image,
    shiftCount: toShiftCount(candidate),
    years: toYearsOfExperience(candidate),
    rating: toRating(candidate?.average_rating),
    attendance: toAttendance(candidate),
    isFavorite: counter?.isFavorite,
    isBestInDental: isBestInDentalWinner(candidate),
    missingSpecialty: hasSpecialtyMismatch(candidate, job) ? 'Missing Specialty' : undefined,
    companyExperience: companyMatch?.label,
    partialShift: isPartialShift(counter) ? 'Partial Shift' : undefined,
    counterRate: hasCounterRate(counter, job) ? 'Counter Rate' : undefined,
  };
};
